import React, { useCallback, useMemo, useState } from "react";
import { FiSmartphone } from "react-icons/fi";
import DataTable from "../common/DataTable";
import TablePagination from "../common/TablePagination";
import useAdminResource from "../../hooks/useAdminResource";
import adminUserService from "../../services/adminUserService";
import { buildDeviceColumns } from "./deviceColumns";

/**
 * Room detail content — the room's devices. Shared by the Detail modal and the
 * /rooms/:roomId route.
 *
 * Reuses buildDeviceColumns with hideRoomColumn, so a device is described
 * identically here and on the Devices tab. The Room column is dropped because
 * every row here is in the same room.
 */
export default function RoomDetailBody({ userId, roomId, onOpenDevice }) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const fetcher = useCallback(
    async () => {
      const res = await adminUserService.fetchRoomDetail(userId, roomId, { page, limit });
      return {
        items: { room: res.room, devices: res.devices },
        pagination: res.pagination,
      };
    },
    [userId, roomId, page, limit]
  );

  const { data, meta, state, error, refetch, dismissError } = useAdminResource(
    fetcher,
    [userId, roomId, page, limit]
  );

  const devices = data?.devices || [];

  const columns = useMemo(
    () =>
      buildDeviceColumns({
        // In a modal there is nothing to navigate to, so clicks are handed
        // upward instead of pushing a route.
        navigate: () => {},
        basePath: "",
        hideRoomColumn: true,
      }),
    []
  );

  return (
    <div>
      <DataTable
        columns={columns}
        data={devices}
        state={state}
        error={error}
        onRetry={() => refetch()}
        onDismissError={dismissError}
        getRowId={(row) => row._id}
        onRowClick={onOpenDevice ? (row) => onOpenDevice(row) : undefined}
        emptyIcon={FiSmartphone}
        emptyTitle="No devices in this room"
        emptyMessage="Nothing is assigned to this room yet."
      />

      <TablePagination
        page={meta.page}
        totalPages={meta.totalPages}
        total={meta.total}
        limit={meta.limit}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
      />
    </div>
  );
}
